<section
  class="business-document-wrapper"
  [class.business-document-observation-surface]="readOnly"
  [class.business-document-review-surface]="inReview"
  [attr.aria-readonly]="readOnly || inReview"
>
  <div
    *ngIf="!pipelineFree"
    class="business-document-workflow"
    [class.business-document-workflow-compact]="compact"
    [class.business-document-workflow-new]="lifecycleState === 'new'"
    [class.business-document-workflow-draft]="lifecycleState === 'draft'"
    [class.business-document-workflow-in-step]="lifecycleState === 'in-step'"
    [class.business-document-workflow-in-review]="lifecycleState === 'in-review'"
    [class.business-document-workflow-failed]="lifecycleState === 'failed'"
    [class.business-document-workflow-successful]="lifecycleState === 'successful'"
  >
    <div class="container-xl">
      <div class="row align-items-center">
        <div class="col-auto">
          <div class="text-secondary py-2" *ngIf="loading">Loading document workflow…</div>
          <div class="alert alert-danger my-2" data-local-alert *ngIf="errorMessage">
            {{ errorMessage }}
            <a
              *ngIf="!businessDocumentId && businessDocumentType && !previewPipeline"
              class="btn btn-sm btn-primary ms-2"
              [routerLink]="['/configuration/business-documents', businessDocumentType]"
            >Set up pipelines</a>
          </div>
        </div>

        <div class="col" *ngIf="!workspace && previewPipeline">
          <app-pipeline-tracker
            class="d-block py-2"
            [stages]="trackerStages"
            [current]="trackerCurrent"
          ></app-pipeline-tracker>
        </div>

        <div *ngIf="!businessDocumentId && businessDocumentType" class="col-auto py-2">
          <div class="d-flex align-items-center gap-2 text-secondary small" *ngIf="previewPipeline as preview">
            <span class="badge" [ngClass]="lifecycleBadgeClass">{{ lifecycleLabel }}</span>
            <strong class="text-body">{{ preview.pipeline.name }}</strong>
            <span>{{ preview.steps.length }} steps</span>
            <span class="badge bg-blue-lt" *ngIf="preview.pipeline.is_default">Default</span>
          </div>
        </div>

        <ng-container *ngIf="workspace">
          <div class="col px-0">
            <app-pipeline-tracker
              class="d-block py-2"
              [stages]="trackerStages"
              [current]="trackerCurrent"
            ></app-pipeline-tracker>
          </div>
          <div class="col-auto py-2">
            <span class="badge" [ngClass]="lifecycleBadgeClass">{{ lifecycleLabel }}</span>
          </div>
          <div class="col-auto py-2" *ngIf="inOutStep">
            <button type="button" class="btn btn-primary" (click)="openDrawer('email', $event.currentTarget)">
              <i class="ti ti-send me-1" aria-hidden="true"></i>
              Send to participant
            </button>
          </div>
        </ng-container>

        <div *ngIf="!businessDocumentId && businessDocumentType" class="col-auto ms-auto py-2 d-print-none">
          <div class="d-flex align-items-center gap-2">
            <span class="text-secondary small">Pipeline</span>
            <app-business-document-pipeline-picker
              [value]="pipelineId"
              [options]="pickerOptions"
              [disabled]="pipelineDisabled || loading"
              ariaLabel="Select document workflow pipeline"
              (valueChange)="choosePipeline($event)"
            ></app-business-document-pipeline-picker>
          </div>
          <small class="invalid-feedback d-block" *ngIf="pipelineError">{{ pipelineError }}</small>
        </div>
      </div>
    </div>
  </div>

  <div
    class="business-document-floating-control d-print-none"
    [class.business-document-floating-control-with-rail]="!!businessDocumentId"
    *ngIf="!pipelineFree && businessDocumentId && workspace && showStepView"
  >
    <span class="text-secondary small">Step view</span>
    <div class="btn-group" role="group" aria-label="Current step view mode">
      <button
        type="button"
        class="btn btn-sm"
        [class.btn-primary]="accessMode === 'observe'"
        [class.btn-ghost-secondary]="accessMode !== 'observe'"
        (click)="setAccessMode('observe')"
      >
        Observation
      </button>
      <button
        type="button"
        class="btn btn-sm"
        [class.btn-primary]="accessMode === 'edit'"
        [class.btn-ghost-secondary]="accessMode !== 'edit'"
        [disabled]="!canUseEditMode"
        (click)="setAccessMode('edit')"
      >
        Edit as {{ editingContributorLabel }}
      </button>
    </div>
  </div>

  <aside
    *ngIf="businessDocumentId"
    class="business-document-action-rail d-print-none"
    aria-label="Business document tools"
  >
    <button
      type="button"
      class="business-document-rail-action business-document-rail-action-review"
      [class.active]="activeDrawer === 'reviews'"
      [class.business-document-rail-action-attention]="inReview || currentStepRequiresVerification"
      aria-label="Open review drawer"
      title="Review"
      (click)="openDrawer('reviews', $event.currentTarget)"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M8 9h8" />
        <path d="M8 13h6" />
        <path d="M9 18h-2a4 4 0 0 1 -4 -4v-5a4 4 0 0 1 4 -4h10a4 4 0 0 1 4 4v5a4 4 0 0 1 -4 4h-3l-5 3v-3z" />
      </svg>
      <span>Review</span>
      <span class="business-document-rail-badge" *ngIf="pendingReviewCount">
        {{ pendingReviewCount }}
      </span>
    </button>
    <button
      type="button"
      class="business-document-rail-action"
      [class.active]="activeDrawer === 'view'"
      aria-label="View document"
      title="View"
      (click)="openDrawer('view', $event.currentTarget)"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
        <path d="M21 12c-2.4 4 -5.4 6 -9 6s-6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6s6.6 2 9 6" />
      </svg>
      <span>View</span>
    </button>
    <button
      type="button"
      class="business-document-rail-action"
      [class.active]="activeDrawer === 'history'"
      aria-label="Open document history"
      title="History"
      (click)="openDrawer('history', $event.currentTarget)"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M12 8l0 4l2 2" />
        <path d="M3.05 11a9 9 0 1 1 .5 4m-.5 4v-4h4" />
      </svg>
      <span>History</span>
    </button>
    <button
      type="button"
      class="business-document-rail-action"
      [class.active]="activeDrawer === 'print'"
      aria-label="Print document"
      title="Print"
      (click)="openDrawer('print', $event.currentTarget)"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
        <path d="M17 9v-4h-10v4" />
        <path d="M7 13h10v8h-10z" />
      </svg>
      <span>Print</span>
    </button>
    <button
      type="button"
      class="business-document-rail-action"
      [class.active]="activeDrawer === 'email'"
      aria-label="Email document"
      title="Email"
      (click)="openDrawer('email', $event.currentTarget)"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M3 7a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2z" />
        <path d="M3 7l9 6l9 -6" />
      </svg>
      <span>Email</span>
    </button>
    <button
      type="button"
      class="business-document-rail-action"
      [class.active]="activeDrawer === 'chat'"
      aria-label="Open document chat"
      title="Chat"
      (click)="openDrawer('chat', $event.currentTarget)"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path d="M8 9h8" /><path d="M8 13h6" />
        <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3z" />
      </svg>
      <span>Chat</span>
    </button>
  </aside>

  <button
    *ngIf="activeDrawer"
    type="button"
    class="business-document-drawer-backdrop d-print-none"
    aria-label="Close document tools"
    (click)="closeDrawer()"
  ></button>

  <aside
    *ngIf="activeDrawer"
    #drawerPanel
    class="business-document-drawer"
    [class.business-document-drawer-wide]="activeDrawer === 'view' || activeDrawer === 'print'"
    [class.business-document-drawer-medium]="activeDrawer === 'reviews' || activeDrawer === 'history' || activeDrawer === 'email' || activeDrawer === 'chat'"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="drawerTitle()"
  >
    <header class="business-document-drawer-header">
      <div>
        <div class="text-secondary small">{{ workspace?.document?.document_number }}</div>
        <h2 class="m-0">{{ drawerTitle() }}</h2>
      </div>
      <button
        type="button"
        class="btn btn-sm btn-icon"
        data-drawer-initial-focus
        aria-label="Close document tools"
        (click)="closeDrawer()"
      >
        <i class="ti ti-x" aria-hidden="true"></i>
      </button>
    </header>

    <div class="business-document-drawer-body">
      <app-verification-drawer
        *ngIf="activeDrawer === 'reviews' && workspace as model"
        [open]="true"
        [embedded]="true"
        [businessDocumentId]="model.document.id"
        [requests]="model.verification_requests"
        (closed)="closeDrawer()"
        (changed)="load()"
      ></app-verification-drawer>

      <app-business-document-view
        *ngIf="activeDrawer === 'view' || activeDrawer === 'print'"
        [businessDocumentIdInput]="businessDocumentId"
        [embedded]="true"
        (closed)="closeDrawer()"
      ></app-business-document-view>

      <app-business-document-events
        *ngIf="activeDrawer === 'history'"
        [documentOwner]="activityDocumentOwner"
        [documentType]="activityDocumentType"
        [documentId]="activityDocumentId"
        [refreshKey]="historyRefreshKey"
        [history]="history"
        title="Document activity"
      ></app-business-document-events>

      <app-business-document-email-drawer
        *ngIf="activeDrawer === 'email' && workspace as model"
        [workspace]="model"
        [printablePayload]="printablePayload()"
      ></app-business-document-email-drawer>

      <app-business-document-chat-drawer
        *ngIf="activeDrawer === 'chat' && workspace as model"
        [workspace]="model"
        [businessDocumentRoute]="viewRoute"
      ></app-business-document-chat-drawer>
    </div>
  </aside>

  <div class="alert alert-warning mx-3" data-local-alert *ngIf="inReview">
    This document is in review. Editing is locked until all required reviews are complete.
  </div>

  <div #contentHost class="business-document-content" [class.business-document-content-with-rail]="!!businessDocumentId">
    <div class="page-body">
      <div class="container-xl">
        <ng-content></ng-content>
      </div>
    </div>
  </div>
</section>
